import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { wrapPromise } from "go-core";
import Header from "go-app/components/Header";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { FormatAddressString } from "go-core/components/Formatters/FormatAddress";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import RenderLimitedText from "go-core/components/RenderLimitedText";
import { useWindowSize } from "go-core/components/useWindowSize";
import { FILTER_SEPARATOR, NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { partnerApi } from "../../../../../../../../../../services/Api/Partner/partnerApi";
import { PartnerApi } from "../../../../../../../../../../services/Api/Partner/partnerTypes";
import { userApi } from "../../../../../../../../../../services/Api/User/userApi";
import { selectPartner } from "../../../../../../../../../../services/partner/selectors";

interface ListState {
	resource?: Record<string, any>;
}

const listName = "SUB_PARTNER";
const resourceType = "SUB_PARTNER";

const List = ({ resource }: ListState) => {
	const segmentContextValue = React.useContext(SegmentContext);
	const partner = useSelector(selectPartner);
	const { t } = useTranslation("admin");

	if (!resource) return null;
	const data = resource.read();

	let config = {
		fields: [
			{
				id: "name",
				name: t("lib:common.word.name"),
			},
			{
				id: "company_details",
				name: `${t("common.word.company_info")}`,
				disableSorting: true,
				render: (item: PartnerApi) => {
					return (
						<RenderLimitedText minWidth={250}>
							<div className={"d-flex flex-column"}>
								<span>{item.company_name}</span>
								<span className={"text-detailed"}>
									{FormatAddressString({
										street: item.company_street,
										build_nr: item.company_build_nr,
										flat_nr: item.company_flat_nr,
										zip_code: item.company_zip_code,
										city: item.company_city,
									})}
								</span>
								{item.company_tax_id_no && (
									<small className={"text-detailed"}>NIP: {item.company_tax_id_no}</small>
								)}
							</div>
						</RenderLimitedText>
					);
				},
				renderExport: (item: PartnerApi) => {
					return `${item.company_name ? item.company_name : ""} ${
						item.company_tax_id_no ? ` ${item.company_tax_id_no}` : ""
					}${item.company_tax_id_no || item.company_name ? ", " : ""} ${FormatAddressString({
						street: item.company_street,
						build_nr: item.company_build_nr,
						flat_nr: item.company_flat_nr,
						zip_code: item.company_zip_code,
						city: item.company_city,
					})}`;
				},
			},
			{
				id: "user",
				name: `${t("lib:common.word.users")}`,
				render: (item: PartnerApi) => {
					return (
						<RenderLimitedText minWidth={300}>
							{item?.users?.map((user) => user.user_email).join(", ")}
						</RenderLimitedText>
					);
				},
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						userApi.getUsersSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "company_contact",
				name: `${t("common.word.contact_info")}`,
				disableSorting: true,
				render: (item: PartnerApi) => {
					return (
						<RenderLimitedText minWidth={250}>
							<div className={"d-flex flex-column"}>
								<span>{item.company_name}</span>
								{item.company_phone_number && (
									<span className={"text-detailed"}>{item.company_phone_number}</span>
								)}
								{item.company_email && <span className={"text-detailed"}>{item.company_email}</span>}
							</div>
						</RenderLimitedText>
					);
				},
				renderExport: (item: PartnerApi) =>
					`${item.contact_person} ${item.company_phone_number} ${item.company_email}`,
			},
			{
				id: "created_at",
				name: t("lib:common.word.created_at"),
				type: "date",
				render: (item: PartnerApi) => FormatDate(item.created_at),
			},
		],
		selectedColumns: ["name", "company_details", "user", "company_contact", "created_at"],
		filters: [
			{
				id: "company_name",
				name: t("common.word.company_name"),
				type: "text",
			},
			{
				id: "company_tax_id_no",
				name: t("lib:common.word.tax_id_no.title"),
				type: "text",
			},
			{
				id: "company_city",
				name: `${t("modules.partner.field.company_city.title")}`,
				type: "text",
			},
			{
				id: "user_email",
				name: t("lib:common.word.email"),
				type: "text",
			},
		],
		actions: [
			{
				name: t("modules.partner.action.show_organizations.title"),
				link: (item: PartnerApi) =>
					`/partnership/${partner?.id}/organizations?f=${btoa(
						unescape(
							encodeURIComponent(
								`${NEW_WAY_TO_ENCODING_FILTER_SIGN}${FILTER_SEPARATOR}partner|e=${item.id}`
							)
						)
					)}`,
			},
		],
		segments: [
			{
				id: "all",
				slug: "all",
				name: t("common.word.all_male"),
			},
			{
				id: "disabled",
				name: t("modules.partner.field.disabled.title"),
				slug: "disabled",
				filters: [
					{
						filterId: "status",
						value: "DISABLED",
						condition: "e",
					},
				],
			},
			{
				id: "deleted",
				name: t("lib:go_list.filters.deleted_male"),
				slug: "deleted",
				filters: [
					{
						filterId: "status",
						value: "DELETED",
						condition: "e",
					},
				],
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params = { ...params, include: "users,any_sub_partner" };
			return partnerApi.getSubPartners(Number(partner?.id), params, {
				cancelToken: sourceToken?.token,
			});
		},
		exportConfig: {
			title: t("common.word.partners"),
			filename: t("modules.partner.field.export_config.filename"),
		},
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;
	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
		filterValues: data.filter_values,
	};

	return <ListData config={config} />;
};

const PartnerPreviewPartnersIndexPage = () => {
	const { t } = useTranslation("admin");
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = React.useContext(SegmentContext);
	const customFieldResourceTypes = [
		{
			type: "PARTNER",
			name: t(`enums.custom_fields.resources.PARTNER`),
		},
	];
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(`${t("common.word.partners")} | ${t("modules.partner.field.partner_panel.title")}`);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType, customFieldResourceTypes)));
	}, []);

	return (
		<>
			{!isMobile && <Header title={t("common.word.partners")} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} />
			</Suspense>
		</>
	);
};

export default PartnerPreviewPartnersIndexPage;
